/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import {
    findAndUpdateUserInfo,
    saveAddedUser,
} from '../../screens/SystemScreens/AdminUsersListScreen/AdminUsersListUtils';
import { UserTableData } from '../../screens/SystemScreens/AdminUsersListScreen/AdminUsersListConfig';
import { AdminUsersAction, AdminUsersActionType } from '../actions/adminUsersActions';
import { PageQueryParams } from '../api/apiCommon.typings';
import {
    DetailedUserDto,
    DetailedUsersListDto,
    DetailedUsersListFilterParams,
    UserStatus,
} from '../api/users/usersClient.typings';

export const rowsPerPage = [20, 30, 40];
export const defaultSearchType: keyof UserTableData = 'name';
export const defaultPaginationLimit = rowsPerPage[0];
export const defaultUserStatus = [UserStatus.Active];

export interface AdminUsersState {
    usersList: DetailedUsersListDto;
    addedUsersList: DetailedUserDto[];
    pageParams: PageQueryParams;
    filterParams: DetailedUsersListFilterParams;
    changedFilterParams: DetailedUsersListFilterParams;
    isLoading: boolean;
    isOpenAddUserDialog: boolean;
    isOpenEditUserDialog: boolean;
}

const initialState: AdminUsersState = {
    usersList: null,
    addedUsersList: [],
    pageParams: {
        limit: defaultPaginationLimit,
        offset: 0,
    },
    filterParams: {
        search_type: defaultSearchType,
        search_text: '',
        status: defaultUserStatus,
        uiType: [],
        roles: [],
        taxAuthorityId: [],
        operatorTaxNumber: [],
    },
    changedFilterParams: null,
    isLoading: false,
    isOpenAddUserDialog: false,
    isOpenEditUserDialog: false,
};

function adminUsersReducer(state: AdminUsersState = initialState, action: AdminUsersAction): AdminUsersState {
    switch (action.type) {
        case AdminUsersActionType.OPEN_ADMIN_USERS_LIST_PAGE:
            return {
                ...state,
                isLoading: true,
            };

        case AdminUsersActionType.APPLY_PAGING:
            return {
                ...state,
                isLoading: true,
                pageParams: action.payload,
                changedFilterParams: null,
            };

        case AdminUsersActionType.APPLY_FILTER:
            return {
                ...state,
                isLoading: true,
                pageParams: { ...state.pageParams, offset: 0 },
                filterParams: state.changedFilterParams ? { ...state.changedFilterParams } : { ...state.filterParams },
                changedFilterParams: null,
            };

        case AdminUsersActionType.CHANGE_FILTERS:
            return {
                ...state,
                changedFilterParams: action.payload,
            };

        case AdminUsersActionType.ADD_USER:
        case AdminUsersActionType.EDIT_USER:
        case AdminUsersActionType.BLOCK_USER:
        case AdminUsersActionType.UNBLOCK_USER:
        case AdminUsersActionType.DELETE_USER:
            return {
                ...state,
                isLoading: true,
            };

        case AdminUsersActionType.ADD_USER_DIALOG_TOGGLE:
            return {
                ...state,
                isOpenAddUserDialog: action.payload,
            };

        case AdminUsersActionType.EDIT_USER_DIALOG_TOGGLE:
            return {
                ...state,
                isOpenEditUserDialog: action.payload,
            };

        case AdminUsersActionType.FETCH_USERS_LIST_SUCCESS:
            return {
                ...state,
                isLoading: false,
                usersList: action.payload.list,
                addedUsersList: [],
            };

        case AdminUsersActionType.FETCH_USERS_LIST_ERROR:
            return {
                ...state,
                isLoading: false,
                usersList: {
                    count: 0,
                    users: [],
                },
            };

        case AdminUsersActionType.UPDATE_USER_SUCCESS:
            return {
                ...state,
                isLoading: false,
                isOpenEditUserDialog: false,
                usersList: findAndUpdateUserInfo(action.payload, state.usersList),
            };

        case AdminUsersActionType.UPDATE_USER_ERROR:
            return {
                ...state,
                isLoading: false,
            };

        case AdminUsersActionType.ADD_USER_SUCCESS:
            return {
                ...state,
                isLoading: false,
                isOpenAddUserDialog: false,
                addedUsersList: saveAddedUser(action.payload.user, state.addedUsersList),
            };

        case AdminUsersActionType.ADD_USER_ERROR:
            return {
                ...state,
                isLoading: false,
            };

        case AdminUsersActionType.DOWNLOAD_EXCEL:
            return {
                ...state,
                isLoading: true,
                changedFilterParams: null,
            };

        case AdminUsersActionType.DOWNLOAD_EXCEL_SUCCESS:
        case AdminUsersActionType.DOWNLOAD_EXCEL_ERROR:
            return {
                ...state,
                isLoading: false,
            };

        case AdminUsersActionType.EXIT_ADMIN_USERS_LIST_PAGE:
            return {
                ...state,
                usersList: null,
                addedUsersList: [],
                isOpenAddUserDialog: false,
                isOpenEditUserDialog: false,
                changedFilterParams: null,
            };

        default:
            return state;
    }
}

export default adminUsersReducer;
